/**
 * SealedCheckboxCell Component
 * Checkbox component for marking orders as sealed in the sealing workflow
 */

import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { useUpdateOrderMutation } from '../../store/slices/apiSlice';
import { useToast } from '../../hooks/useToast';
import type { OrderSummary } from '../../utils/types';

interface SealedCheckboxCellProps {
  order: OrderSummary;
  className?: string;
  onSealedChange?: (orderId: string, sealed: boolean) => void;
}

export const SealedCheckboxCell: React.FC<SealedCheckboxCellProps> = ({ 
  order, 
  className = '',
  onSealedChange 
}) => {
  const [updateOrder, { isLoading }] = useUpdateOrderMutation();
  const toast = useToast();
  
  // Get current sealed status from metadata
  const currentSealed = order.metadata?.sealing_workflow?.sealed ?? false;
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSealedToggle = async () => {
    if (isLoading || isUpdating) return;
    
    setIsUpdating(true);
    const newSealedStatus = !currentSealed;
    
    try {
      // Update the metadata with sealing workflow status
      const updatedMetadata = {
        ...order.metadata,
        sealing_workflow: {
          ...order.metadata?.sealing_workflow,
          sealed: newSealedStatus,
          sealed_at: newSealedStatus ? new Date().toISOString() : null,
          sealed_by: newSealedStatus ? 'current_user' : null // This would be replaced with actual user info
        }
      };
      
      await updateOrder({ 
        id: order.id, 
        data: { metadata: updatedMetadata }
      }).unwrap();
      
      toast.success(
        newSealedStatus ? 'Order Sealed' : 'Sealing Unchecked', 
        newSealedStatus 
          ? 'Order marked as sealed - print label and rack options now available' 
          : 'Sealing status removed'
      );
      
      // Notify parent component of the change
      onSealedChange?.(order.id, newSealedStatus);
      
    } catch (e: any) {
      toast.error('Update failed', e?.data?.detail || 'Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const cellClasses = [
    'p-2 border',
    'text-center',
    className
  ].filter(Boolean).join(' ');

  return (
    <td className={cellClasses}>
      <div className="flex items-center justify-center">
        <div className="flex items-center">
          <label className="relative cursor-pointer group">
            <input
              type="checkbox"
              checked={currentSealed}
              onChange={handleSealedToggle}
              disabled={isLoading || isUpdating}
              className="sr-only"
            />
            <div 
              className={`
                w-5 h-5 border-2 rounded flex items-center justify-center transition-all duration-200
                ${currentSealed 
                  ? 'bg-green-500 border-green-500' 
                  : 'border-gray-300 hover:border-green-400 group-hover:bg-green-50'
                }
                ${(isLoading || isUpdating) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {currentSealed && (
                <Check className="w-3 h-3 text-white" strokeWidth={3} />
              )}
            </div>
          </label>
          {(isLoading || isUpdating) && (
            <div className="ml-2 w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
          )}
        </div>
      </div>
    </td>
  );
};

SealedCheckboxCell.displayName = 'SealedCheckboxCell';
